/**
 * StructuralHealthCard - Tier 1 Structural Analysis Display
 *
 * Displays deterministic structural analysis results with expandable
 * explanations for each metric tied to the actual analysis data.
 *
 * @version 1.1.0
 * @date 2026-02-04
 */

import React, { useState } from 'react';

// =============================================================================
// TYPES
// =============================================================================

interface PropertyCoverage {
  count: number;
  total: number;
  percentage: number;
  severity: 'ok' | 'warning' | 'critical';
}

interface HierarchyDepth {
  value: number;
  assessment: string;
  severity: 'ok' | 'warning' | 'critical';
}

interface InversePropertyAnalysis {
  count: number;
  severity: 'ok' | 'warning' | 'critical';
}

interface PropertyTypeDistribution {
  object: number;
  datatype: number;
  annotation: number;
  objectPercent: number;
  datatypePercent: number;
  annotationPercent: number;
}

// Controlled Vocabulary Analysis Types
interface ControlledVocabularyCandidate {
  propertyUri: string;
  propertyLabel: string;
  matchedPattern: string;
  currentRange?: string;
  hasShaclEnumeration: boolean;
  recommendation: string;
}

interface ControlledVocabularyAnalysis {
  candidates: ControlledVocabularyCandidate[];
  totalStringProperties: number;
  candidateCount: number;
  severity: 'ok' | 'warning' | 'critical';
}

// SHACL Consistency Analysis Types
interface UndeclaredProperty {
  propertyUri: string;
  usedOnClasses: string[];
  inferredType: 'object' | 'datatype' | 'unknown';
}

interface PhantomClassReference {
  referencedClassUri: string;
  referencedFrom: { className: string; propertyPath: string }[];
  suggestedMatch?: string;
}

interface MalformedIRI {
  uri: string;
  location: string;
  issue: string;
  suggestedFix?: string;
}

interface ShaclConsistencyAnalysis {
  undeclaredProperties: UndeclaredProperty[];
  phantomClasses: PhantomClassReference[];
  malformedIRIs: MalformedIRI[];
  dualTypedClasses: number;
  blankNodeCount: number;
}

// Annotation Coverage Types
interface AnnotationCoverageStat {
  count: number;
  total: number;
  percentage: number;
}

interface AnnotationCoverage {
  rdfsLabel: AnnotationCoverageStat;
  rdfsComment: AnnotationCoverageStat;
  skosDefinition: AnnotationCoverageStat;
  toolingWarning?: string;
}

// Disjointness Analysis Types
interface DisjointnessAnalysis {
  siblingGroups: { parent: string; siblings: string[]; hasDisjointness: boolean }[];
  missingDisjointnessCount: number;
}

// Root Class Validation Types
interface RootClassValidation {
  rootClasses: string[];
  anomalies: { classUri: string; parentUri: string; issue: string }[];
}

// Entity Completeness Analysis Types
interface MissingEntity {
  booleanPropertyUri: string;
  booleanPropertyLabel: string;
  inferredEntityName: string;
  pattern: string;
  recommendation: string;
  pharmaRelevance?: string;
}

interface DomainEntityGap {
  expectedEntity: string;
  domain: string;
  rationale: string;
  relatedExistingClasses: string[];
}

interface EntityCompletenessAnalysis {
  missingEntities: MissingEntity[];
  domainGaps: DomainEntityGap[];
  detectedDomain?: string;
  severity: 'ok' | 'warning' | 'critical';
}

export interface StructuralAnalysis {
  score: number;
  propertiesWithDomain: PropertyCoverage;
  propertiesWithRange: PropertyCoverage;
  hierarchyDepth: HierarchyDepth;
  inverseProperties: InversePropertyAnalysis;
  externalNamespaces: string[];
  orphanClasses: string[];
  classPropertyRatio: number;
  propertyTypeDistribution: PropertyTypeDistribution;
  hierarchyBreadth: Record<string, number>;
  analyzedAt: string;
  // Tier 1 analyses
  controlledVocabularyIssues?: ControlledVocabularyAnalysis;
  entityCompleteness?: EntityCompletenessAnalysis;
  shaclConsistency?: ShaclConsistencyAnalysis;
  annotationCoverage?: AnnotationCoverage;
  disjointnessAnalysis?: DisjointnessAnalysis;
  rootClassValidation?: RootClassValidation;
}

interface StructuralHealthCardProps {
  analysis: StructuralAnalysis | null;
  loading: boolean;
  error: string | null;
  onReanalyze?: () => void;
  playbookEntries?: PlaybookEntry[];
}

// =============================================================================
// METRIC EXPLANATIONS
// =============================================================================

interface MetricExplanation {
  title: string;
  whatItMeans: string;
  whyItMatters: string;
  getFindings: (analysis: StructuralAnalysis) => string;
  getRecommendation: (analysis: StructuralAnalysis) => string | null;
}

const METRIC_EXPLANATIONS: Record<string, MetricExplanation> = {
  propertiesWithDomain: {
    title: 'Properties with Domain',
    whatItMeans: 'Domain declarations specify which classes a property can be used with. For example, if "createdBy" has domain "Document", it means only Document instances can have a createdBy relationship.',
    whyItMatters: 'Without domain declarations, properties can be accidentally used on any class, leading to inconsistent data. Reasoners cannot infer class membership, and graph traversal becomes unpredictable.',
    getFindings: (a) => {
      if (a.propertiesWithDomain.percentage === 0) {
        return `None of your ${a.propertiesWithDomain.total} properties have domain declarations. This means any class can use any property, which prevents meaningful reasoning and validation.`;
      }
      if (a.propertiesWithDomain.percentage < 50) {
        return `Only ${a.propertiesWithDomain.count} of ${a.propertiesWithDomain.total} properties (${a.propertiesWithDomain.percentage}%) have domain declarations. The majority of your properties can be used on any class.`;
      }
      return `${a.propertiesWithDomain.count} of ${a.propertiesWithDomain.total} properties (${a.propertiesWithDomain.percentage}%) have domain declarations.`;
    },
    getRecommendation: (a) => {
      if (a.propertiesWithDomain.percentage < 80) {
        return 'Add rdfs:domain declarations to your properties. For each property, identify which classes it applies to and declare that as the domain.';
      }
      return null;
    },
  },
  propertiesWithRange: {
    title: 'Properties with Range',
    whatItMeans: 'Range declarations specify what type of values a property can have. For object properties, this is another class. For datatype properties, this is a data type like string or date.',
    whyItMatters: 'Without range declarations, there\'s no constraint on what values can be assigned. This prevents validation, breaks autocomplete in tools, and makes the ontology harder to use correctly.',
    getFindings: (a) => {
      if (a.propertiesWithRange.percentage === 0) {
        return `None of your ${a.propertiesWithRange.total} properties have range declarations. Property values are completely unconstrained.`;
      }
      if (a.propertiesWithRange.percentage < 50) {
        return `Only ${a.propertiesWithRange.count} of ${a.propertiesWithRange.total} properties (${a.propertiesWithRange.percentage}%) have range declarations.`;
      }
      return `${a.propertiesWithRange.count} of ${a.propertiesWithRange.total} properties (${a.propertiesWithRange.percentage}%) have range declarations.`;
    },
    getRecommendation: (a) => {
      if (a.propertiesWithRange.percentage < 80) {
        return 'Add rdfs:range declarations to specify valid values. For object properties, use the target class. For datatype properties, use XSD types like xsd:string or xsd:dateTime.';
      }
      return null;
    },
  },
  hierarchyDepth: {
    title: 'Hierarchy Depth',
    whatItMeans: 'This measures how many levels deep your class hierarchy goes, from root classes down to the most specific subclasses.',
    whyItMatters: 'A well-structured hierarchy (3-5 levels) enables inheritance of properties, supports meaningful classification, and improves query performance. Too shallow means everything is at the same level; too deep becomes hard to navigate.',
    getFindings: (a) => {
      if (a.hierarchyDepth.value === 0) {
        return 'Your ontology has no class hierarchy - all classes are at the root level with no subclass relationships.';
      }
      if (a.hierarchyDepth.value <= 2) {
        return `Your hierarchy is ${a.hierarchyDepth.value} levels deep, which is relatively shallow. Most classes are direct children of root classes.`;
      }
      if (a.hierarchyDepth.value > 6) {
        return `Your hierarchy is ${a.hierarchyDepth.value} levels deep, which may be overly complex. Deep hierarchies can be difficult to navigate and maintain.`;
      }
      return `Your hierarchy is ${a.hierarchyDepth.value} levels deep, which provides good organization for navigation and inheritance.`;
    },
    getRecommendation: (a) => {
      if (a.hierarchyDepth.value <= 2) {
        return 'Consider adding intermediate categories to group related classes. For example, create an "Agent" superclass for User, Organization, and System classes.';
      }
      if (a.hierarchyDepth.value > 6) {
        return 'Consider flattening some branches of your hierarchy. Very deep hierarchies can indicate over-classification.';
      }
      return null;
    },
  },
  inverseProperties: {
    title: 'Inverse Properties',
    whatItMeans: 'Inverse properties are bidirectional relationships. If "createdBy" has an inverse "creates", then stating "Document createdBy User" automatically implies "User creates Document".',
    whyItMatters: 'Without inverse declarations, you can only traverse relationships in one direction. This limits query flexibility and requires redundant data entry to express both directions.',
    getFindings: (a) => {
      if (a.inverseProperties.count === 0) {
        return 'No inverse property relationships are declared. All relationships are one-directional only.';
      }
      return `${a.inverseProperties.count} inverse property relationships are declared, enabling bidirectional traversal.`;
    },
    getRecommendation: (a) => {
      if (a.inverseProperties.count === 0) {
        return 'Identify relationships that make sense in both directions (e.g., "hasParent" / "hasChild", "createdBy" / "creates") and declare owl:inverseOf relationships.';
      }
      return null;
    },
  },
  externalNamespaces: {
    title: 'External Namespaces',
    whatItMeans: 'External namespaces indicate your ontology references or extends other established ontologies (like Dublin Core, FOAF, or domain-specific standards).',
    whyItMatters: 'Reusing external vocabularies promotes interoperability - your data can be understood by other systems using the same standards. It also reduces redundant definitions.',
    getFindings: (a) => {
      if (a.externalNamespaces.length === 0) {
        return 'Your ontology does not reference any external namespaces. It exists in isolation from established vocabularies.';
      }
      return `Your ontology references ${a.externalNamespaces.length} external namespace${a.externalNamespaces.length > 1 ? 's' : ''}: ${a.externalNamespaces.slice(0, 5).join(', ')}${a.externalNamespaces.length > 5 ? ` and ${a.externalNamespaces.length - 5} more` : ''}.`;
    },
    getRecommendation: (a) => {
      if (a.externalNamespaces.length === 0) {
        return 'Consider aligning with upper ontologies like BFO (Basic Formal Ontology) for life sciences, or reusing common vocabularies like Dublin Core for metadata.';
      }
      return null;
    },
  },
  orphanClasses: {
    title: 'Orphan Classes',
    whatItMeans: 'Orphan classes have no parent class (superclass) and no child classes (subclasses). They sit alone in the hierarchy without any taxonomic relationships.',
    whyItMatters: 'Orphan classes cannot inherit properties from parent classes and don\'t participate in the class hierarchy. This often indicates incomplete modeling or classes that should be related to others.',
    getFindings: (a) => {
      if (a.orphanClasses.length === 0) {
        return 'All classes are connected in the hierarchy - no orphan classes detected.';
      }
      return `Found ${a.orphanClasses.length} orphan class${a.orphanClasses.length > 1 ? 'es' : ''}: ${a.orphanClasses.slice(0, 5).join(', ')}${a.orphanClasses.length > 5 ? ` and ${a.orphanClasses.length - 5} more` : ''}.`;
    },
    getRecommendation: (a) => {
      if (a.orphanClasses.length > 0) {
        return 'Review orphan classes and either connect them to the hierarchy via rdfs:subClassOf, or determine if they should be removed.';
      }
      return null;
    },
  },
  classPropertyRatio: {
    title: 'Class-Property Ratio',
    whatItMeans: 'This ratio compares the number of classes to the number of properties. A balanced ontology typically has 0.3 to 2.0 classes per property.',
    whyItMatters: 'Too many classes with few properties suggests an over-elaborate taxonomy. Too few classes with many properties may indicate missing intermediate concepts.',
    getFindings: (a) => {
      if (a.classPropertyRatio > 2.0) {
        return `Your ratio of ${a.classPropertyRatio.toFixed(2)} indicates more classes than properties. The ontology may be over-classified with insufficient relationships defined.`;
      }
      if (a.classPropertyRatio < 0.3) {
        return `Your ratio of ${a.classPropertyRatio.toFixed(2)} indicates many properties relative to classes. Consider whether intermediate classes are needed.`;
      }
      return `Your ratio of ${a.classPropertyRatio.toFixed(2)} indicates a healthy balance between classes and properties.`;
    },
    getRecommendation: (a) => {
      if (a.classPropertyRatio > 2.0) {
        return 'Add more properties to describe relationships between your classes, or consolidate similar classes.';
      }
      if (a.classPropertyRatio < 0.3) {
        return 'Consider adding intermediate classes to better organize your properties, or review if all properties are necessary.';
      }
      return null;
    },
  },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

const getSeverityIcon = (severity: 'ok' | 'warning' | 'critical') => {
  switch (severity) {
    case 'ok':
      return '✅';
    case 'warning':
      return '⚠️';
    case 'critical':
      return '🔴';
    default:
      return '•';
  }
};

const getSeverityClass = (severity: 'ok' | 'warning' | 'critical') => {
  switch (severity) {
    case 'ok':
      return 'severity-ok';
    case 'warning':
      return 'severity-warning';
    case 'critical':
      return 'severity-critical';
    default:
      return '';
  }
};

const getScoreColor = (score: number): string => {
  if (score >= 80) return '#10b981';
  if (score >= 60) return '#f59e0b';
  if (score >= 40) return '#f97316';
  return '#ef4444';
};

// =============================================================================
// EXPANDABLE METRIC COMPONENT
// =============================================================================

// Mapping from metric keys to FAIR playbook indicator IDs
const METRIC_PLAYBOOK_MAP: Record<string, { indicatorId: string; indicatorName: string }> = {
  propertiesWithDomain: { indicatorId: 'S1.1', indicatorName: 'Missing Domain/Range Declarations' },
  propertiesWithRange: { indicatorId: 'S1.1', indicatorName: 'Missing Domain/Range Declarations' },
  hierarchyDepth: { indicatorId: 'S1.4', indicatorName: 'Flat Hierarchy' },
  inverseProperties: { indicatorId: 'S1.2', indicatorName: 'No Inverse Properties' },
  orphanClasses: { indicatorId: 'S1.7', indicatorName: 'Orphan Classes' },
  externalNamespaces: { indicatorId: 'S1.5', indicatorName: 'No Upper Ontology Alignment' },
  controlledVocabulary: { indicatorId: 'S1.3', indicatorName: 'String-Typed Properties Need Controlled Vocabularies' },
  shaclConsistency: { indicatorId: 'S1.6', indicatorName: 'SHACL Shape Inconsistencies' },
};

interface ExpandableMetricProps {
  metricKey: string;
  severity: 'ok' | 'warning' | 'critical';
  label: string;
  value: React.ReactNode;
  assessment?: string;
  badge?: { text: string; type: 'critical' | 'medium' };
  analysis: StructuralAnalysis;
  details?: React.ReactNode;
  playbookEntries?: PlaybookEntry[];
}

interface PlaybookEntry {
  indicator_id: string;
  indicator_name: string;
  diagnosis: string;
  remediation_steps: Array<{
    step_number: number;
    title: string;
    description: string;
    code_language?: string;
    code_snippet?: string;
  }>;
  effort_estimate: string;
}

const ExpandableMetric: React.FC<ExpandableMetricProps> = ({
  metricKey,
  severity,
  label,
  value,
  assessment,
  badge,
  analysis,
  details,
  playbookEntries,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [showPlaybook, setShowPlaybook] = useState(false);
  const explanation = METRIC_EXPLANATIONS[metricKey];
  const playbookRef = METRIC_PLAYBOOK_MAP[metricKey];
  const playbook = playbookRef && playbookEntries?.find(p => p.indicator_id === playbookRef.indicatorId);

  return (
    <div className={`structural-metric expandable ${getSeverityClass(severity)} ${expanded ? 'expanded' : ''}`}>
      <button
        className="metric-header"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
      >
        <span className="metric-icon">{getSeverityIcon(severity)}</span>
        <div className="metric-content">
          <span className="metric-label">{label}</span>
          <span className="metric-value">{value}</span>
          {assessment && <span className="metric-assessment">{assessment}</span>}
          {details}
        </div>
        {badge && (
          <span className={`metric-badge ${badge.type}`}>{badge.text}</span>
        )}
        <span className={`expand-icon ${expanded ? 'expanded' : ''}`}>▼</span>
      </button>

      {expanded && explanation && (
        <div className="metric-explanation">
          <div className="explanation-section">
            <h4>What this measures</h4>
            <p>{explanation.whatItMeans}</p>
          </div>
          <div className="explanation-section">
            <h4>Why it matters</h4>
            <p>{explanation.whyItMatters}</p>
          </div>
          <div className="explanation-section findings">
            <h4>Your ontology</h4>
            <p>{explanation.getFindings(analysis)}</p>
          </div>
          {explanation.getRecommendation(analysis) && (
            <div className="explanation-section recommendation">
              <h4>Recommendation</h4>
              <p>{explanation.getRecommendation(analysis)}</p>
            </div>
          )}
          {playbook && severity !== 'ok' && (
            <div className="explanation-section playbook">
              <button
                className="playbook-toggle"
                onClick={(e) => { e.stopPropagation(); setShowPlaybook(!showPlaybook); }}
              >
                <span>{showPlaybook ? '▾' : '▸'}</span>
                <h4>How to Fix ({playbook.effort_estimate.replace('_', ' ')})</h4>
              </button>
              {showPlaybook && (
                <div className="playbook-content">
                  <p className="playbook-diagnosis">{playbook.diagnosis}</p>
                  <ol className="playbook-steps">
                    {playbook.remediation_steps.map((step) => (
                      <li key={step.step_number}>
                        <strong>{step.title}</strong>
                        <p>{step.description}</p>
                        {step.code_snippet && (
                          <pre className="playbook-code"><code>{step.code_snippet}</code></pre>
                        )}
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// =============================================================================
// EXPANDABLE ANALYSIS CARD (for detailed analysis sections)
// =============================================================================

interface ExpandableAnalysisCardProps {
  severity: 'ok' | 'warning' | 'critical';
  title: string;
  value: string;
  children: React.ReactNode;
}

const ExpandableAnalysisCard: React.FC<ExpandableAnalysisCardProps> = ({
  severity,
  title,
  value,
  children,
}) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={`structural-metric expandable ${getSeverityClass(severity)} ${expanded ? 'expanded' : ''}`}>
      <button
        className="metric-header"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
      >
        <span className="metric-icon">{getSeverityIcon(severity)}</span>
        <div className="metric-content">
          <span className="metric-label">{title}</span>
          <span className="metric-value">{value}</span>
        </div>
        <span className={`expand-icon ${expanded ? 'expanded' : ''}`}>▼</span>
      </button>

      {expanded && (
        <div className="metric-explanation">
          {children}
        </div>
      )}
    </div>
  );
};

// =============================================================================
// FINDINGS LIST WITH SHOW ALL/LESS
// =============================================================================

function FindingsList<T>({
  items,
  limit,
  renderItem,
  noun,
}: {
  items: T[];
  limit: number;
  renderItem: (item: T, index: number) => React.ReactNode;
  noun: string;
}) {
  const [showAll, setShowAll] = useState(false);
  const displayed = showAll ? items : items.slice(0, limit);
  const hasMore = items.length > limit;

  return (
    <div className="findings-list">
      {displayed.map((item, idx) => renderItem(item, idx))}
      {hasMore && (
        <button
          className="show-all-toggle"
          onClick={() => setShowAll(!showAll)}
        >
          {showAll ? `Show fewer ${noun}` : `Show all ${items.length} ${noun}`}
        </button>
      )}
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const StructuralHealthCard: React.FC<StructuralHealthCardProps> = ({
  analysis,
  loading,
  error,
  onReanalyze,
  playbookEntries,
}) => {
  if (loading) {
    return (
      <div className="structural-health-card structural-health-loading">
        <div className="structural-loading-content">
          <div className="spinner-small" />
          <p>Analyzing ontology structure...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="structural-health-card structural-health-error">
        <div className="error-icon">⚠️</div>
        <p>{error}</p>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="structural-health-card structural-health-empty">
        <p>No structural analysis available</p>
      </div>
    );
  }

  const {
    score,
    propertiesWithDomain,
    propertiesWithRange,
    hierarchyDepth,
    inverseProperties,
    externalNamespaces,
    orphanClasses,
    classPropertyRatio,
    controlledVocabularyIssues,
    entityCompleteness,
    shaclConsistency,
    annotationCoverage,
    disjointnessAnalysis,
    rootClassValidation,
  } = analysis;

  return (
    <div className="structural-health-card">
      {/* Header with Score */}
      <div className="structural-health-header">
        <div className="structural-health-title">
          <span className="structural-icon">📊</span>
          <h3>Structural Health Score</h3>
        </div>
        <div
          className="structural-score"
          style={{ color: getScoreColor(score) }}
        >
          {score}/100
        </div>
      </div>

      <p className="structural-hint">Click any metric to learn more</p>

      {/* Metrics Grid */}
      <div className="structural-metrics">
        {/* Properties with Domain */}
        <ExpandableMetric
          metricKey="propertiesWithDomain"
          severity={propertiesWithDomain.severity}
          label="Properties with Domain"
          value={`${propertiesWithDomain.percentage}% (${propertiesWithDomain.count} of ${propertiesWithDomain.total})`}
          badge={propertiesWithDomain.severity === 'critical' ? { text: 'CRITICAL', type: 'critical' } : undefined}
          analysis={analysis}
          playbookEntries={playbookEntries}
        />

        {/* Properties with Range */}
        <ExpandableMetric
          metricKey="propertiesWithRange"
          severity={propertiesWithRange.severity}
          label="Properties with Range"
          value={`${propertiesWithRange.percentage}% (${propertiesWithRange.count} of ${propertiesWithRange.total})`}
          badge={propertiesWithRange.severity === 'critical' ? { text: 'CRITICAL', type: 'critical' } : undefined}
          analysis={analysis}
          playbookEntries={playbookEntries}
        />

        {/* Hierarchy Depth */}
        <ExpandableMetric
          metricKey="hierarchyDepth"
          severity={hierarchyDepth.severity}
          label="Hierarchy Depth"
          value={`${hierarchyDepth.value} levels`}
          assessment={hierarchyDepth.assessment}
          analysis={analysis}
          playbookEntries={playbookEntries}
        />

        {/* Inverse Properties */}
        <ExpandableMetric
          metricKey="inverseProperties"
          severity={inverseProperties.severity}
          label="Inverse Properties"
          value={`${inverseProperties.count} declared`}
          badge={inverseProperties.severity !== 'ok' ? { text: 'MEDIUM', type: 'medium' } : undefined}
          analysis={analysis}
          playbookEntries={playbookEntries}
        />

        {/* External Namespaces */}
        <ExpandableMetric
          metricKey="externalNamespaces"
          severity={externalNamespaces.length > 0 ? 'ok' : 'warning'}
          label="External Namespaces"
          value={`${externalNamespaces.length} referenced`}
          analysis={analysis}
          details={
            externalNamespaces.length > 0 && (
              <span className="metric-details">
                {externalNamespaces.slice(0, 3).join(', ')}
                {externalNamespaces.length > 3 && ` +${externalNamespaces.length - 3} more`}
              </span>
            )
          }
        />

        {/* Orphan Classes */}
        <ExpandableMetric
          metricKey="orphanClasses"
          severity={orphanClasses.length === 0 ? 'ok' : 'warning'}
          label="Orphan Classes"
          value={orphanClasses.length.toString()}
          analysis={analysis}
          details={
            orphanClasses.length > 0 && (
              <span className="metric-details">
                {orphanClasses.slice(0, 3).join(', ')}
                {orphanClasses.length > 3 && ` +${orphanClasses.length - 3} more`}
              </span>
            )
          }
        />

        {/* Class-Property Ratio */}
        <ExpandableMetric
          metricKey="classPropertyRatio"
          severity="ok"
          label="Class-Property Ratio"
          value={classPropertyRatio.toFixed(2)}
          assessment={
            classPropertyRatio >= 0.3 && classPropertyRatio <= 2.0
              ? 'Healthy balance'
              : classPropertyRatio > 2.0
              ? 'More classes than properties'
              : 'More properties than classes'
          }
          analysis={analysis}
          playbookEntries={playbookEntries}
        />
        {/* Controlled Vocabulary Candidates */}
        {controlledVocabularyIssues && (
          <ExpandableAnalysisCard
            severity={controlledVocabularyIssues.severity}
            title="Controlled Vocabulary Candidates"
            value={`${controlledVocabularyIssues.candidateCount} found`}
          >
            <div className="explanation-section">
              <h4>What this detects</h4>
              <p>Scans string-typed properties for names matching categorical value patterns (status, severity, priority, phase, level, category). These properties likely need controlled vocabularies instead of free-text input.</p>
            </div>
            <div className="explanation-section">
              <h4>Why it matters</h4>
              <p>Free-text strings for categorical data cause inconsistency (e.g., &quot;High&quot;, &quot;HIGH&quot;, &quot;high&quot;). Controlled vocabularies via sh:in enumerations or SKOS concept schemes enforce valid values and enable reliable filtering.</p>
            </div>
            {controlledVocabularyIssues.candidates.length > 0 ? (
              <>
                <div className="explanation-section findings">
                  <h4>Your ontology</h4>
                  <p>{controlledVocabularyIssues.candidateCount} of {controlledVocabularyIssues.totalStringProperties} string properties match controlled vocabulary patterns.</p>
                </div>
                <div className="explanation-section recommendation">
                  <h4>Recommendation</h4>
                  <p>Define controlled vocabularies using sh:in enumerations or SKOS concept schemes for each candidate property.</p>
                </div>
                <FindingsList
                  items={controlledVocabularyIssues.candidates}
                  limit={5}
                  noun="candidates"
                  renderItem={(candidate, idx) => (
                    <div key={idx} className="finding-item">
                      <div className="finding-header">
                        <span className="finding-label">{candidate.propertyLabel}</span>
                        <span className="finding-pattern">{candidate.matchedPattern}</span>
                      </div>
                      <p className="finding-recommendation">{candidate.recommendation}</p>
                    </div>
                  )}
                />
              </>
            ) : (
              <div className="explanation-section findings">
                <h4>Your ontology</h4>
                <p>No string properties matching controlled vocabulary patterns were found. Analyzed {controlledVocabularyIssues.totalStringProperties} string/datatype properties.</p>
              </div>
            )}
          </ExpandableAnalysisCard>
        )}

        {/* Entity Completeness Analysis */}
        {entityCompleteness && (
          <ExpandableAnalysisCard
            severity={entityCompleteness.severity}
            title="Missing Entity Analysis"
            value={entityCompleteness.missingEntities.length + entityCompleteness.domainGaps.length > 0
              ? `${entityCompleteness.missingEntities.length + entityCompleteness.domainGaps.length} gaps`
              : 'No gaps'}
          >
            <div className="explanation-section">
              <h4>What this detects</h4>
              <p>Detects boolean properties (e.g., hasCapa, requiresInvestigation) that reference entities not modeled as classes. Also checks for expected domain-specific entities.</p>
            </div>
            <div className="explanation-section">
              <h4>Why it matters</h4>
              <p>Boolean properties like &quot;hasCapa&quot; imply a CAPA entity should exist as a class. Missing entity classes prevent proper relationship modeling and limit the ontology&apos;s expressiveness.</p>
            </div>
            {entityCompleteness.missingEntities.length === 0 && entityCompleteness.domainGaps.length === 0 ? (
              <div className="explanation-section findings">
                <h4>Your ontology</h4>
                <p>No missing entities detected. All entities referenced in boolean properties exist as classes.</p>
              </div>
            ) : (
              <>
                <div className="explanation-section findings">
                  <h4>Your ontology</h4>
                  <p>{entityCompleteness.missingEntities.length} entities inferred from boolean properties are not modeled as classes.{entityCompleteness.domainGaps.length > 0 && ` ${entityCompleteness.domainGaps.length} expected domain entities are also missing.`}</p>
                </div>
                <div className="explanation-section recommendation">
                  <h4>Recommendation</h4>
                  <p>Create OWL classes for each inferred entity and model the relationship using object properties instead of boolean flags.</p>
                </div>
                {entityCompleteness.missingEntities.length > 0 && (
                  <FindingsList
                    items={entityCompleteness.missingEntities}
                    limit={5}
                    noun="missing entities"
                    renderItem={(entity, idx) => (
                      <div key={idx} className="finding-item">
                        <div className="finding-header">
                          <span className="finding-label">{entity.inferredEntityName}</span>
                          <span className="finding-pattern">from {entity.pattern}</span>
                        </div>
                        <p className="finding-source">Detected from: {entity.booleanPropertyLabel}</p>
                        {entity.pharmaRelevance && (
                          <p className="finding-pharma">{entity.pharmaRelevance}</p>
                        )}
                        <p className="finding-recommendation">{entity.recommendation}</p>
                      </div>
                    )}
                  />
                )}
                {entityCompleteness.domainGaps.length > 0 && (
                  <FindingsList
                    items={entityCompleteness.domainGaps}
                    limit={3}
                    noun="domain gaps"
                    renderItem={(gap, idx) => (
                      <div key={idx} className="finding-item">
                        <div className="finding-header">
                          <span className="finding-label">{gap.expectedEntity}</span>
                          <span className="finding-pattern">{gap.domain}</span>
                        </div>
                        <p className="finding-recommendation">{gap.rationale}</p>
                      </div>
                    )}
                  />
                )}
              </>
            )}
          </ExpandableAnalysisCard>
        )}

        {/* SHACL Consistency Analysis */}
        {shaclConsistency && (() => {
          const totalIssues = shaclConsistency.undeclaredProperties.length + shaclConsistency.phantomClasses.length + shaclConsistency.malformedIRIs.length;
          const severity: 'ok' | 'warning' | 'critical' = totalIssues === 0 ? 'ok' : totalIssues > 10 ? 'critical' : 'warning';
          return (
            <ExpandableAnalysisCard
              severity={severity}
              title="SHACL Consistency"
              value={totalIssues > 0 ? `${totalIssues} issues` : 'No issues'}
            >
              <div className="explanation-section">
                <h4>What this detects</h4>
                <p>Cross-references SHACL shape declarations against declared properties and classes to find undeclared properties, phantom class references, and malformed IRIs.</p>
              </div>
              <div className="explanation-section">
                <h4>Why it matters</h4>
                <p>SHACL shapes referencing undeclared properties or classes create silent validation gaps. Data may pass validation despite being structurally incomplete.</p>
              </div>
              {totalIssues === 0 ? (
                <div className="explanation-section findings">
                  <h4>Your ontology</h4>
                  <p>No SHACL consistency issues detected. All shape references are properly declared.</p>
                </div>
              ) : (
                <>
                  <div className="explanation-section findings">
                    <h4>Your ontology</h4>
                    <p>
                      {shaclConsistency.undeclaredProperties.length > 0 && `${shaclConsistency.undeclaredProperties.length} undeclared properties. `}
                      {shaclConsistency.phantomClasses.length > 0 && `${shaclConsistency.phantomClasses.length} phantom class references. `}
                      {shaclConsistency.malformedIRIs.length > 0 && `${shaclConsistency.malformedIRIs.length} malformed IRIs. `}
                      {shaclConsistency.dualTypedClasses > 0 && `${shaclConsistency.dualTypedClasses} dual-typed classes. `}
                      {shaclConsistency.blankNodeCount > 0 && `${shaclConsistency.blankNodeCount} blank nodes.`}
                    </p>
                  </div>
                  <div className="explanation-section recommendation">
                    <h4>Recommendation</h4>
                    <p>Declare missing properties with rdfs:domain and rdfs:range. Add missing class definitions or correct phantom references.</p>
                  </div>
                  {shaclConsistency.undeclaredProperties.length > 0 && (
                    <>
                      <p className="analysis-description">{shaclConsistency.undeclaredProperties.length} undeclared properties:</p>
                      <FindingsList
                        items={shaclConsistency.undeclaredProperties}
                        limit={5}
                        noun="undeclared properties"
                        renderItem={(prop, idx) => (
                          <div key={idx} className="finding-item">
                            <div className="finding-header">
                              <span className="finding-label">{prop.propertyUri.split(/[#/]/).pop()}</span>
                              <span className="finding-pattern">{prop.inferredType}</span>
                            </div>
                            <p className="finding-recommendation">Used on: {prop.usedOnClasses.join(', ')}</p>
                          </div>
                        )}
                      />
                    </>
                  )}
                  {shaclConsistency.phantomClasses.length > 0 && (
                    <>
                      <p className="analysis-description">{shaclConsistency.phantomClasses.length} phantom class references:</p>
                      <FindingsList
                        items={shaclConsistency.phantomClasses}
                        limit={5}
                        noun="phantom classes"
                        renderItem={(phantom, idx) => (
                          <div key={idx} className="finding-item">
                            <div className="finding-header">
                              <span className="finding-label">{phantom.referencedClassUri.split(/[#/]/).pop()}</span>
                              {phantom.suggestedMatch && (
                                <span className="finding-pattern">possible match: {phantom.suggestedMatch}</span>
                              )}
                            </div>
                            <p className="finding-recommendation">Referenced from: {phantom.referencedFrom.map(r => r.className).join(', ')}</p>
                          </div>
                        )}
                      />
                    </>
                  )}
                  {shaclConsistency.malformedIRIs.length > 0 && (
                    <>
                      <p className="analysis-description">{shaclConsistency.malformedIRIs.length} malformed IRIs:</p>
                      <FindingsList
                        items={shaclConsistency.malformedIRIs}
                        limit={5}
                        noun="malformed IRIs"
                        renderItem={(iri, idx) => (
                          <div key={idx} className="finding-item">
                            <div className="finding-header">
                              <span className="finding-label">{iri.uri.split(/[#/]/).pop()}</span>
                            </div>
                            <p className="finding-recommendation">{iri.issue}</p>
                            {iri.suggestedFix && <p className="finding-source">Fix: {iri.suggestedFix}</p>}
                          </div>
                        )}
                      />
                    </>
                  )}
                </>
              )}
            </ExpandableAnalysisCard>
          );
        })()}

        {/* Annotation Coverage */}
        {annotationCoverage && (() => {
          const severity: 'ok' | 'warning' | 'critical' = annotationCoverage.rdfsComment.percentage > 50 ? 'ok' : annotationCoverage.rdfsComment.percentage === 0 ? 'critical' : 'warning';
          return (
            <ExpandableAnalysisCard
              severity={severity}
              title="Annotation Coverage"
              value={`${annotationCoverage.rdfsLabel.percentage}% labels, ${annotationCoverage.rdfsComment.percentage}% descriptions`}
            >
              <div className="explanation-section">
                <h4>What this measures</h4>
                <p>Checks how many classes and properties have rdfs:label and rdfs:comment (or skos:definition) annotations.</p>
              </div>
              <div className="explanation-section">
                <h4>Why it matters</h4>
                <p>Labels are essential for human-readable display in tools. Comments and definitions help users understand the intended meaning, reducing misinterpretation and misuse.</p>
              </div>
              <div className="explanation-section findings">
                <h4>Your ontology</h4>
                <p>{annotationCoverage.rdfsLabel.count} of {annotationCoverage.rdfsLabel.total} elements have labels ({annotationCoverage.rdfsLabel.percentage}%). {annotationCoverage.rdfsComment.count} of {annotationCoverage.rdfsComment.total} elements have descriptions ({annotationCoverage.rdfsComment.percentage}%).</p>
              </div>
              {severity !== 'ok' && (
                <div className="explanation-section recommendation">
                  <h4>Recommendation</h4>
                  <p>Add rdfs:comment descriptions to all classes and properties. Use clear, concise definitions that explain the intended meaning.</p>
                </div>
              )}
              {annotationCoverage.toolingWarning && (
                <div className="explanation-section recommendation">
                  <h4>Tooling warning</h4>
                  <p>{annotationCoverage.toolingWarning}</p>
                </div>
              )}
            </ExpandableAnalysisCard>
          );
        })()}

        {/* Disjointness Analysis */}
        {disjointnessAnalysis && disjointnessAnalysis.siblingGroups.length > 0 && (() => {
          const severity: 'ok' | 'warning' | 'critical' = disjointnessAnalysis.missingDisjointnessCount === 0 ? 'ok' : 'warning';
          return (
            <ExpandableAnalysisCard
              severity={severity}
              title="Disjointness Axioms"
              value={disjointnessAnalysis.missingDisjointnessCount > 0
                ? `${disjointnessAnalysis.missingDisjointnessCount} of ${disjointnessAnalysis.siblingGroups.length} groups missing`
                : `All ${disjointnessAnalysis.siblingGroups.length} groups covered`}
            >
              <div className="explanation-section">
                <h4>What this detects</h4>
                <p>Identifies sibling classes (sharing the same parent) that lack owl:disjointWith declarations.</p>
              </div>
              <div className="explanation-section">
                <h4>Why it matters</h4>
                <p>Without disjointness axioms, an individual can be classified under multiple sibling types simultaneously, preventing reasoners from detecting classification errors.</p>
              </div>
              {disjointnessAnalysis.missingDisjointnessCount > 0 ? (
                <>
                  <div className="explanation-section findings">
                    <h4>Your ontology</h4>
                    <p>{disjointnessAnalysis.missingDisjointnessCount} of {disjointnessAnalysis.siblingGroups.length} sibling groups lack disjointness declarations.</p>
                  </div>
                  <div className="explanation-section recommendation">
                    <h4>Recommendation</h4>
                    <p>Add owl:disjointWith declarations between sibling classes, or use owl:AllDisjointClasses for groups of 3+.</p>
                  </div>
                  <FindingsList
                    items={disjointnessAnalysis.siblingGroups.filter(g => !g.hasDisjointness)}
                    limit={4}
                    noun="sibling groups"
                    renderItem={(group, idx) => (
                      <div key={idx} className="finding-item">
                        <div className="finding-header">
                          <span className="finding-label">Children of {group.parent}</span>
                          <span className="finding-pattern">{group.siblings.length} siblings</span>
                        </div>
                        <p className="finding-recommendation">
                          {group.siblings.slice(0, 4).join(', ')}{group.siblings.length > 4 ? ` +${group.siblings.length - 4} more` : ''}
                        </p>
                      </div>
                    )}
                  />
                </>
              ) : (
                <div className="explanation-section findings">
                  <h4>Your ontology</h4>
                  <p>All {disjointnessAnalysis.siblingGroups.length} sibling groups have disjointness axioms declared.</p>
                </div>
              )}
            </ExpandableAnalysisCard>
          );
        })()}

        {/* Root Class Validation */}
        {rootClassValidation && (() => {
          const severity: 'ok' | 'warning' | 'critical' = rootClassValidation.anomalies.length === 0 ? 'ok' : rootClassValidation.anomalies.length > 3 ? 'critical' : 'warning';
          return (
            <ExpandableAnalysisCard
              severity={severity}
              title="Root Class Validation"
              value={rootClassValidation.anomalies.length > 0
                ? `${rootClassValidation.anomalies.length} anomalies`
                : `${rootClassValidation.rootClasses.length} root classes, no anomalies`}
            >
              <div className="explanation-section">
                <h4>What this detects</h4>
                <p>Validates root class parent references for non-standard or anonymized values that may indicate modeling errors.</p>
              </div>
              <div className="explanation-section">
                <h4>Why it matters</h4>
                <p>Root classes with non-standard parents (e.g., owl:NeutralConcept00 instead of owl:Thing) indicate anonymization artifacts or incorrect ontology construction.</p>
              </div>
              {rootClassValidation.anomalies.length > 0 ? (
                <>
                  <div className="explanation-section findings">
                    <h4>Your ontology</h4>
                    <p>{rootClassValidation.anomalies.length} root class anomalies detected in {rootClassValidation.rootClasses.length} root classes.</p>
                  </div>
                  <div className="explanation-section recommendation">
                    <h4>Recommendation</h4>
                    <p>Correct non-standard parent references to use owl:Thing or remove them if they are anonymization artifacts.</p>
                  </div>
                  <FindingsList
                    items={rootClassValidation.anomalies}
                    limit={5}
                    noun="anomalies"
                    renderItem={(anomaly, idx) => (
                      <div key={idx} className="finding-item">
                        <div className="finding-header">
                          <span className="finding-label">{anomaly.classUri.split(/[#/]/).pop()}</span>
                        </div>
                        <p className="finding-recommendation">{anomaly.issue}</p>
                      </div>
                    )}
                  />
                </>
              ) : (
                <div className="explanation-section findings">
                  <h4>Your ontology</h4>
                  <p>{rootClassValidation.rootClasses.length} root classes found, all with valid parent references.</p>
                </div>
              )}
            </ExpandableAnalysisCard>
          );
        })()}
      </div>

      {/* Footer with timestamp and re-analyze button */}
      <div className="structural-footer">
        <span className="analyzed-at">
          Analyzed: {new Date(analysis.analyzedAt).toLocaleString()}
        </span>
        {onReanalyze && (
          <button
            className="reanalyze-button"
            onClick={onReanalyze}
            title="Re-analyze with latest rules"
          >
            Re-analyze
          </button>
        )}
      </div>
    </div>
  );
};

export default StructuralHealthCard;
